import { getStore } from "app/store/store"
import { IUserInfo, IUserRole } from "user/model"
import { userRPO } from "user/repository"
import { isImpersonating } from "./impersonate"

export const isSystemAdmin = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("admin0permissions") !== -1

export const isSspUserSession = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("user_ssp") !== -1

export const canReadCampaigns = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("admin_dsp_campaign") !== -1

export const canSaveCampaigns = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("campaigns0save") !== -1

export const canSaveOrder = (userInfo?: IUserInfo) => canSaveCampaigns(userInfo)

export const canSaveAdvertisers = (userInfo?: IUserInfo) => canSaveCampaigns(userInfo)

export const canAccessForecast = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("admin_dsp_planning") !== -1

export const canAccessDspDeal = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("user_ssp") !== -1

export const canAccessReports = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("admin_dsp_reports") !== -1

export const canViewAgencyPage = (agencyId: number | null): boolean => agencyId !== null && !isSystemAdmin()

export const isAgencyAdmin = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("user0agency_save") !== -1

export const isDspUser = async (userId: number, roles?: IUserRole[]): Promise<boolean> =>
  (roles || (await userRPO.getUserRoles(userId))).includes("dsp.user")

export const isSspUser = async (userId: number, roles?: IUserRole[]): Promise<boolean> =>
  (roles || (await userRPO.getUserRoles(userId))).includes("ssp.user")

export const isDspSale = async (userId: number, roles?: IUserRole[]): Promise<boolean> =>
  (roles || (await userRPO.getUserRoles(userId))).includes("dsp.sale")

export const isDspReports = async (userId: number, roles?: IUserRole[]): Promise<boolean> =>
  (roles || (await userRPO.getUserRoles(userId))).includes("dsp.reports")

export const addDspRole = async (userId: number, role: IUserRole) => userRPO.addUserRole(userId, role)

export const removeDspRole = async (userId: number, role: IUserRole) => userRPO.deleteUserRole(userId, role)

// DSP USER
export const canSaveUserSelf = (userInfo?: IUserInfo) =>
  !isImpersonating() &&
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("user0self_save") !== -1

export const canSavePasswordSelf = (userInfo?: IUserInfo) =>
  !isImpersonating() &&
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("user0self_password") !== -1

// APP ADMIN

export const setUserAppAdmin = async (userId: number, isAdmin: boolean): Promise<void> => {
  if (isSspUserSession()) {
    return setUserSspAdmin(userId, isAdmin)
  }
  return setUserDspAdmin(userId, isAdmin)
}

export const setUserDspAdmin = async (userId: number, isAdmin: boolean): Promise<void> =>
  await (isAdmin ? userRPO.addUserRole(userId, "dsp.admin") : userRPO.deleteUserRole(userId, "dsp.admin"))

export const setUserSspAdmin = async (userId: number, isAdmin: boolean): Promise<void> =>
  await (isAdmin ? userRPO.addUserRole(userId, "ssp.admin") : userRPO.deleteUserRole(userId, "ssp.admin"))

export const isAppAdmin = async (userId: number, roles?: IUserRole[]): Promise<boolean> => {
  roles = roles || (await userRPO.getUserRoles(userId))
  if (roles.includes("ssp.user")) {
    return isSspAdmin(userId, roles)
  }
  return isDspAdmin(userId, roles)
}

export const isDspAdmin = async (userId: number, roles?: IUserRole[]): Promise<boolean> =>
  (roles || (await userRPO.getUserRoles(userId))).includes("dsp.admin")

export const isSspAdmin = async (userId: number, roles?: IUserRole[]): Promise<boolean> =>
  (roles || (await userRPO.getUserRoles(userId))).includes("ssp.admin")

// only update, can't change admin role
export const canSaveUserAgency = (userInfo?: IUserInfo) =>
  (userInfo?.permissions || getStore().getState().user.permissions).indexOf("user0agency_save") !== -1

export const canSaveUserRoleAgency = (userInfo?: IUserInfo) => {
  const permissions = userInfo?.permissions || getStore().getState().user.permissions
  return (
    permissions.indexOf("user0dsp_admin_access_level") !== -1 ||
    permissions.indexOf("user0ssp_admin_access_level") !== -1
  )
}

export const getUserRoles = (userId: number): Promise<IUserRole[]> => userRPO.getUserRoles(userId)
